// TheRace — complete rewrite with per-circuit shapes
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Constants ─────────────────────────────────────────────────────────────────
const TOTAL = 50;

// Normalized [x,y] 0-1 waypoints — sharp F1 shapes with hairpins & straights
const CIRCUITS = [
  {
    name: 'Monaco', flag: '🇲🇨', color: '#e10600', bg: '#0a0a18',
    desc: 'Circuit de rue · Technique',
    // Tight street circuit: S/F bottom-right, Station Hairpin left, Casino top
    waypoints: [
      [0.72,0.84],[0.86,0.72],[0.83,0.55],[0.72,0.35],
      [0.58,0.14],[0.42,0.1],[0.25,0.18],
      [0.11,0.32],[0.09,0.44],[0.15,0.54], // Station hairpin
      [0.28,0.66],[0.46,0.82],[0.58,0.88],[0.68,0.88],
    ],
  },
  {
    name: 'Silverstone', flag: '🇬🇧', color: '#10b981', bg: '#0a140a',
    desc: 'Home of Racing · Rapide',
    // Wing shape: long pit straight, Becketts complex top, Luffield bottom-left
    waypoints: [
      [0.56,0.88],[0.78,0.82],[0.9,0.72],[0.88,0.6],[0.9,0.48],
      [0.82,0.34],[0.88,0.2],[0.78,0.08],[0.6,0.05],
      [0.42,0.05],[0.24,0.1],[0.1,0.22],[0.07,0.42],
      [0.12,0.62],[0.28,0.78],[0.42,0.88],
    ],
  },
  {
    name: 'Monza', flag: '🇮🇹', color: '#f59e0b', bg: '#140e00',
    desc: 'Temple de la Vitesse · Chicanes',
    // Fast oblong + zigzag chicanes on the right side
    waypoints: [
      [0.5,0.9],[0.68,0.88],[0.82,0.8],[0.78,0.7],[0.86,0.6], // chicane 1
      [0.82,0.5],[0.88,0.38],[0.8,0.22],[0.65,0.1],            // chicane 2
      [0.48,0.08],[0.3,0.1],[0.14,0.22],[0.1,0.42],
      [0.12,0.62],[0.2,0.78],[0.36,0.88],
    ],
  },
  {
    name: 'Spa', flag: '🇧🇪', color: '#3b82f6', bg: '#050a18',
    desc: 'Eau Rouge · Kemmel · Classique',
    // Triangle: La Source top-right, Raidillon valley left, long Kemmel straight
    waypoints: [
      [0.62,0.88],[0.8,0.78],[0.88,0.62],[0.8,0.5],  // La Source hairpin
      [0.65,0.38],[0.5,0.22],[0.34,0.12],              // Kemmel straight
      [0.2,0.18],[0.1,0.34],[0.1,0.52],[0.18,0.68],   // Pouhon / Paul Frere
      [0.32,0.82],[0.48,0.9],[0.56,0.9],               // Bus Stop
    ],
  },
  {
    name: 'Suzuka', flag: '🇯🇵', color: '#ec4899', bg: '#140a12',
    desc: 'Figure-8 · S-Curves · 130R',
    // Figure-8 crossing in the middle
    waypoints: [
      [0.65,0.88],[0.8,0.8],[0.88,0.66],[0.82,0.52],[0.88,0.38], // S-curves right
      [0.78,0.26],[0.65,0.18],[0.5,0.18],[0.38,0.28],[0.34,0.42], // hairpin left
      [0.42,0.52],[0.52,0.56],                                     // crossover
      [0.38,0.64],[0.3,0.74],[0.36,0.84],[0.5,0.9],              // 130R + chicane
    ],
  },
  {
    name: 'Interlagos', flag: '🇧🇷', color: '#22c55e', bg: '#041208',
    desc: 'Senna S · Anti-sens · Brésil',
    // Narrow figure-of-merit shape; anti-clockwise
    waypoints: [
      [0.52,0.88],[0.68,0.82],[0.78,0.7],[0.7,0.58],[0.8,0.46],
      [0.84,0.32],[0.76,0.18],[0.62,0.1],[0.45,0.08],
      [0.28,0.12],[0.14,0.26],[0.1,0.45],[0.14,0.64],
      [0.26,0.8],[0.4,0.88],
    ],
  },
];

const SPECIAL_CASES = {
  4:  { type: 'DRS',         emoji: '🚀', label: 'Zone DRS',       color: '#3b82f6' },
  8:  { type: 'OBSTACLE',    emoji: '🚧', label: 'Décors',          color: '#f59e0b' },
  12: { type: 'TURBO',       emoji: '🔥', label: 'Turbo',           color: '#f97316' },
  16: { type: 'OVERTAKE',    emoji: '⚡', label: 'Overtake',        color: '#10b981' },
  19: { type: 'RAIN',        emoji: '🌧', label: 'Pluie',           color: '#60a5fa' },
  22: { type: 'OVERSTEER',   emoji: '🔄', label: 'Survirage',       color: '#ef4444' },
  26: { type: 'PIT_STOP',    emoji: '🔧', label: 'Pit Stop',        color: '#8b5cf6' },
  30: { type: 'SAFETY_CAR',  emoji: '�', label: 'Safety Car',      color: '#fbbf24' },
  34: { type: 'CRASH',       emoji: '💥', label: 'Crash',           color: '#ef4444' },
  37: { type: 'ENGINE_FAIL', emoji: '🔩', label: 'Casse moteur',    color: '#dc2626' },
  41: { type: 'FASTEST_LAP', emoji: '🏆', label: 'Fastest Lap',     color: '#ffd700' },
  44: { type: 'DRS',         emoji: '🚀', label: 'Zone DRS',        color: '#3b82f6' },
  47: { type: 'MYSTERY',     emoji: '❓', label: 'Mystery Box',     color: '#ec4899' },
  49: { type: 'TURBO',       emoji: '🔥', label: 'Turbo final',     color: '#f97316' },
};

const XP_REWARDS = { 1: 1000, 2: 600, 3: 300 };

// ── Path utilities ─────────────────────────────────────────────────────────────
const interpolatePath = (pts, n) => {
  const closed = [...pts, pts[0]];
  const segLens = [];
  let total = 0;
  for (let i = 1; i < closed.length; i++) {
    const d = Math.hypot(closed[i][0]-closed[i-1][0], closed[i][1]-closed[i-1][1]);
    segLens.push(d); total += d;
  }
  const step = total / n;
  const result = [];
  let segIdx = 0, distAccum = 0;
  for (let k = 0; k < n; k++) {
    const target = k * step;
    while (segIdx < segLens.length - 1 && distAccum + segLens[segIdx] < target) {
      distAccum += segLens[segIdx]; segIdx++;
    }
    const t = segLens[segIdx] > 0 ? (target - distAccum) / segLens[segIdx] : 0;
    result.push([
      closed[segIdx][0] + t * (closed[segIdx+1][0] - closed[segIdx][0]),
      closed[segIdx][1] + t * (closed[segIdx+1][1] - closed[segIdx][1]),
    ]);
  }
  return result;
};

const buildSmoothPath = (pts) => {
  const closed = [...pts, pts[0]];
  let d = `M ${closed[0][0].toFixed(2)},${closed[0][1].toFixed(2)}`;
  for (let i = 1; i < closed.length; i++) {
    d += ` L ${closed[i][0].toFixed(2)},${closed[i][1].toFixed(2)}`;
  }
  return d;
};

const scaleWp = (waypoints, W, H, pad) =>
  waypoints.map(([nx,ny]) => [pad + nx*(W-2*pad), pad + ny*(H-2*pad)]);


// ── Dice face SVG ─────────────────────────────────────────────────────────────
const DICE_DOTS = {
  1: [[50,50]],
  2: [[28,28],[72,72]],
  3: [[28,28],[50,50],[72,72]],
  4: [[28,28],[72,28],[28,72],[72,72]],
  5: [[28,28],[72,28],[50,50],[28,72],[72,72]],
  6: [[28,28],[72,28],[28,50],[72,50],[28,72],[72,72]],
};
const DiceFace = ({ value, size = 56, rolling, accentColor = '#e10600' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100">
    <rect x="3" y="3" width="94" height="94" rx="16"
      fill={rolling ? '#1a1a1a' : '#111'} stroke={rolling ? '#555' : accentColor + '88'} strokeWidth="3" />
    {value && (DICE_DOTS[value]||[]).map(([cx,cy],i) => (
      <circle key={i} cx={cx} cy={cy} r="10" fill={rolling ? '#444' : '#fff'} />
    ))}
  </svg>
);

// ── Track SVG ─────────────────────────────────────────────────────────────────
const TrackBoard = ({ game, circuitCfg }) => {
  const W = 340, H = 240, PAD = 18;
  const wp = circuitCfg?.waypoints || CIRCUITS[0].waypoints;
  const scaled = scaleWp(wp, W, H, PAD);
  const casePts = interpolatePath(scaled, TOTAL);
  const pathD   = buildSmoothPath(scaled);
  const accent  = circuitCfg?.color || '#e10600';

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display:'block' }}>
      {/* Background */}
      <rect width={W} height={H} fill={circuitCfg?.bg || '#0a0a12'} rx="12" />

      {/* Track asphalt layers */}
      <path d={pathD} fill="none" stroke="#444" strokeWidth="22" strokeLinecap="round" strokeLinejoin="round" />
      <path d={pathD} fill="none" stroke="#252525" strokeWidth="18" strokeLinecap="round" strokeLinejoin="round" />
      {/* Kerb / edge highlights */}
      <path d={pathD} fill="none" stroke={accent + '30'} strokeWidth="22" strokeLinecap="round" strokeLinejoin="round" />
      <path d={pathD} fill="none" stroke="#1a1a1a" strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" />
      {/* Center line dashes */}
      <path d={pathD} fill="none" stroke="#383838" strokeWidth="1.5" strokeDasharray="10 8" />

      {/* Case markers */}
      {casePts.map((pt, i) => {
        const caseNum = i + 1;
        const sp = SPECIAL_CASES[caseNum];
        const isStart = i === 0;

        if (isStart) return (
          <g key={i}>
            <rect x={pt[0]-9} y={pt[1]-6} width={18} height={12} rx="2" fill="#fff" stroke={accent} strokeWidth="2" />
            <text x={pt[0]} y={pt[1]+3.5} textAnchor="middle" fontSize="5.5" fontWeight="bold" fill={accent} style={{userSelect:'none'}}>S/F</text>
          </g>
        );

        if (sp) return (
          <g key={i}>
            <circle cx={pt[0]} cy={pt[1]} r={11} fill={sp.color + '33'} />
            <circle cx={pt[0]} cy={pt[1]} r={8}  fill={sp.color + 'cc'} stroke="#fff" strokeWidth="1" />
            <text x={pt[0]} y={pt[1]+4} textAnchor="middle" fontSize="9" style={{userSelect:'none'}}>{sp.emoji}</text>
          </g>
        );

        const isMilestone = caseNum % 10 === 0;
        return (
          <g key={i}>
            {isMilestone && <circle cx={pt[0]} cy={pt[1]} r={7} fill={accent + '22'} stroke={accent + '55'} strokeWidth="1" />}
            <circle cx={pt[0]} cy={pt[1]} r={isMilestone ? 4 : 2.5} fill={isMilestone ? accent + 'aa' : '#484848'} />
            {isMilestone && (
              <text x={pt[0]} y={pt[1]-8} textAnchor="middle" fontSize="6" fill={accent + 'aa'} style={{userSelect:'none'}}>{caseNum}</text>
            )}
          </g>
        );
      })}

      {/* Player tokens */}
      {game.players.map((player, pi) => {
        const posIdx = player.position % TOTAL;
        const pt = casePts[posIdx];
        if (!pt) return null;
        const tokenOffsets = [[0,-18],[16,-10],[-16,-10],[0,-30]];
        const [ox, oy] = tokenOffsets[pi % tokenOffsets.length];
        const isFinished = player.isFinished;
        return (
          <g key={pi} style={{ filter: `drop-shadow(0 0 5px ${player.color})` }}>
            <circle cx={pt[0]+ox} cy={pt[1]+oy+1} r={11} fill="#000" opacity="0.35" />
            <circle cx={pt[0]+ox} cy={pt[1]+oy} r={11} fill={player.color}
              stroke={isFinished ? '#FFD600' : '#fff'} strokeWidth={isFinished ? 2.5 : 1.5} />
            <text x={pt[0]+ox} y={pt[1]+oy+4} textAnchor="middle" fontSize="7.5" fontWeight="bold" fill="#fff" style={{userSelect:'none'}}>
              {player.username.slice(0,2).toUpperCase()}
            </text>
            {isFinished && (
              <text x={pt[0]+ox} y={pt[1]+oy-15} textAnchor="middle" fontSize="10">🏁</text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ── Lobby ─────────────────────────────────────────────────────────────────────
const Lobby = ({ onStart, myGames, onResume, currentUserId }) => {
  const [circuit, setCircuit] = useState('Monaco');
  const [laps, setLaps] = useState(1);
  const [creating, setCreating] = useState(false);
  const [friends, setFriends] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const selCfg = CIRCUITS.find(c => c.name === circuit) || CIRCUITS[0];

  useEffect(() => {
    api.get('/friends').then(r => setFriends(r.data)).catch(() => {});
  }, []);

  const toggleFriend = (id) =>
    setSelectedFriends(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 5 ? [...prev, id] : prev
    );

  const start = async () => {
    setCreating(true);
    await onStart(circuit, laps, selectedFriends);
    setCreating(false);
  };

  return (
    <div className="space-y-5 pb-8">
      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${selCfg.bg} 0%, #1a0505 100%)`, border: `1px solid ${selCfg.color}33` }}>
        <div className="px-5 pt-6 pb-5 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 mb-1">P1 or Not P1</p>
          <h1 className="text-4xl font-black text-white mb-1" style={{ letterSpacing: '-0.02em' }}>
            THE <span style={{ color: '#e10600' }}>RACE</span>
          </h1>
          <p className="text-zinc-400 text-sm">Plateau F1 · 50 cases · Tour par tour</p>
        </div>
        <div className="absolute top-2 right-4 opacity-[0.06] text-[100px] pointer-events-none select-none leading-none">🏎</div>
      </div>

      {/* Circuit selection */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">Circuit</p>
        <div className="grid grid-cols-2 gap-2">
          {CIRCUITS.map(c => (
            <button key={c.name} onClick={() => setCircuit(c.name)}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all text-left"
              style={{
                borderColor: circuit === c.name ? c.color : '#3f3f46',
                background: circuit === c.name ? c.color + '18' : '#131316',
                boxShadow: circuit === c.name ? `0 0 14px ${c.color}33` : 'none',
              }}>
              <span className="text-lg leading-none">{c.flag}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black text-white leading-none">{c.name}</p>
                <p className="text-[9px] text-zinc-600 mt-0.5 truncate">{c.desc}</p>
              </div>
              {circuit === c.name && (
                <span className="text-[10px] font-black shrink-0" style={{ color: c.color }}>✓</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lap selection */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">Nombre de tours</p>
        <div className="grid grid-cols-3 gap-2">
          {[1,2,3].map(n => (
            <button key={n} onClick={() => setLaps(n)}
              className="py-3 rounded-xl border font-black text-sm transition-all"
              style={{
                borderColor: laps === n ? selCfg.color : '#3f3f46',
                background: laps === n ? selCfg.color + '18' : '#131316',
                color: laps === n ? selCfg.color : '#6b7280',
                boxShadow: laps === n ? `0 0 12px ${selCfg.color}33` : 'none',
              }}>
              {n} {n === 1 ? 'tour' : 'tours'}
              <p className="text-[9px] font-normal mt-0.5 opacity-60">{n * 50} cases</p>
            </button>
          ))}
        </div>
      </div>

      {/* Friend picker */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
          Inviter des amis <span className="normal-case font-normal text-zinc-600">({selectedFriends.length}/5 sélectionné{selectedFriends.length>1?'s':''})</span>
        </p>
        {friends.length === 0 ? (
          <p className="text-xs text-zinc-600 italic">Tu n'as pas encore d'amis — ajoute-en depuis ton profil !</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {friends.map(f => {
              const sel = selectedFriends.includes(f._id);
              return (
                <button key={f._id} onClick={() => toggleFriend(f._id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border transition-all text-left"
                  style={{
                    borderColor: sel ? selCfg.color : '#3f3f46',
                    background: sel ? selCfg.color + '18' : '#131316',
                    boxShadow: sel ? `0 0 10px ${selCfg.color}22` : 'none',
                  }}>
                  {f.avatar
                    ? <img src={f.avatar} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                    : <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-black text-white shrink-0">
                        {f.username.slice(0,1).toUpperCase()}
                      </div>
                  }
                  <span className="text-xs font-bold text-white truncate flex-1">{f.username}</span>
                  {sel && <span className="text-[10px] font-black shrink-0" style={{color:selCfg.color}}>✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Start button */}
      <button onClick={start} disabled={creating}
        className="w-full py-4 rounded-2xl font-black text-white text-lg transition-all active:scale-95"
        style={{
          background: creating ? '#333' : `linear-gradient(135deg, ${selCfg.color}, ${selCfg.color}99)`,
          boxShadow: creating ? 'none' : `0 6px 28px ${selCfg.color}44`,
        }}>
        {creating ? 'Démarrage…' : selectedFriends.length > 0
          ? `🏁 LANCER (${1 + selectedFriends.length} joueurs)`
          : `🏁 SOLO — ${selCfg.flag} ${circuit}`
        }
      </button>

      {/* XP info */}
      <div className="rounded-xl border border-zinc-800/80 bg-zinc-900/40 p-3 grid grid-cols-3 divide-x divide-zinc-800 text-center">
        {[['🥇','1er','+1000 XP','#FFD600'],['🥈','2e','+600 XP','#A8A8B3'],['🥉','3e','+300 XP','#CD7F32']].map(([ic,rank,xp,col]) => (
          <div key={rank} className="px-2">
            <p className="text-lg">{ic}</p>
            <p className="text-xs font-black" style={{color:col}}>{rank}</p>
            <p className="text-[10px] text-zinc-500">{xp}</p>
          </div>
        ))}
      </div>

      {/* Cases legend */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">Cases spéciales · {Object.keys(SPECIAL_CASES).length} sur 50</p>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.values(SPECIAL_CASES).filter((v,i,a) => a.findIndex(x=>x.type===v.type)===i).map(sp => (
            <div key={sp.type} className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
              style={{ background: sp.color + '12', border: `1px solid ${sp.color}33` }}>
              <span className="text-sm">{sp.emoji}</span>
              <span className="text-[10px] font-bold" style={{color:sp.color}}>{sp.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-zinc-800/50 border border-zinc-700/50">
            <span className="text-sm">🟢</span>
            <span className="text-[10px] font-bold text-zinc-400">Case 10/20/30/40</span>
          </div>
        </div>
      </div>

      {/* Resume past games */}
      {myGames.filter(g => g.status === 'playing').length > 0 && (
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">Parties en cours</p>
          <div className="space-y-2">
            {myGames.filter(g => g.status === 'playing').map(g => {
              const cfg = CIRCUITS.find(c => c.name === g.circuit) || CIRCUITS[0];
              const totalCases = (g.laps || 1) * TOTAL;
              const me = g.players.find(p => String(p.user) === String(currentUserId));
              const done = ((me?.lapsCompleted || 0) * TOTAL) + (me?.position || 0);
              const isMyTurn = String(g.players[g.currentPlayerIndex]?.user) === String(currentUserId);
              return (
                <button key={g._id} onClick={() => onResume(g._id)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left"
                  style={{ borderColor: isMyTurn ? cfg.color : cfg.color + '44', background: cfg.bg,
                    boxShadow: isMyTurn ? `0 0 14px ${cfg.color}33` : 'none' }}>
                  <span className="text-2xl">{cfg.flag}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-bold text-white">{g.circuit}</p>
                      <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                        style={{background: cfg.color+'22', color: cfg.color}}>{g.laps||1}T · {g.players.length}J</span>
                      {isMyTurn && <span className="text-[9px] font-black animate-pulse" style={{color:cfg.color}}>▶ TON TOUR</span>}
                    </div>
                    <div className="flex items-center gap-1 mb-1">
                      {g.players.map((p,i) => (
                        <span key={i} className="text-[9px] font-bold truncate"
                          style={{color: String(p.user)===String(currentUserId) ? '#fff' : '#6b7280'}}>
                          {p.username.slice(0,8)}{i < g.players.length-1 ? ' ·' : ''}
                        </span>
                      ))}
                    </div>
                    <div className="h-1 rounded-full bg-zinc-800 overflow-hidden">
                      <div className="h-full rounded-full" style={{width:`${Math.min(100,(done/totalCases)*100)}%`,background:cfg.color}} />
                    </div>
                  </div>
                  <span className="text-xs font-black shrink-0" style={{color:cfg.color}}>→</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Game view ─────────────────────────────────────────────────────────────────
const GameView = ({ gameId, onBack }) => {
  const { user } = useAuth();
  const [game, setGame]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(false);
  const [diceAnim, setDiceAnim] = useState(null);
  const [showEvent, setShowEvent] = useState(null);
  const eventTimer = useRef(null);

  const fetchGame = useCallback(async () => {
    try { const { data } = await api.get(`/race-game/${gameId}`); setGame(data); } catch {}
  }, [gameId]);

  useEffect(() => { fetchGame().finally(() => setLoading(false)); }, [fetchGame]);

  useEffect(() => {
    if (!game || game.status !== 'playing') return;
    const myIdx = game.players.findIndex(p => String(p.user) === String(user?.id));
    if (myIdx === game.currentPlayerIndex) return;
    const t = setInterval(fetchGame, 3000);
    return () => clearInterval(t);
  }, [game, user, fetchGame]);

  const handleRoll = async () => {
    if (rolling) return;
    setRolling(true);
    let frame = 0;
    const anim = setInterval(() => {
      setDiceAnim(Math.floor(Math.random()*6)+1);
      if (++frame >= 10) clearInterval(anim);
    }, 60);
    try {
      const { data } = await api.post(`/race-game/${gameId}/roll`);
      setTimeout(() => {
        setDiceAnim(data.lastRoll);
        setGame(data);
        if (data.lastEvent) {
          setShowEvent(data.lastEvent);
          clearTimeout(eventTimer.current);
          eventTimer.current = setTimeout(() => setShowEvent(null), 4000);
        } else { setShowEvent(null); }
        setRolling(false);
      }, 600);
    } catch { clearInterval(anim); setRolling(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-48 text-zinc-500 text-sm">Chargement…</div>;
  if (!game)   return <div className="text-center text-zinc-500 py-12">Partie introuvable</div>;

  const circuitCfg = CIRCUITS.find(c => c.name === game.circuit) || CIRCUITS[0];
  const myPlayer   = game.players.find(p => String(p.user) === String(user?.id));
  const curPlayer  = game.players[game.currentPlayerIndex];
  const isMyTurn   = String(curPlayer?.user) === String(user?.id);
  const totalCases = (game.laps || 1) * TOTAL;

  return (
    <div className="space-y-3 pb-8">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="text-zinc-600 hover:text-white transition-colors text-sm px-2 py-1 rounded-lg hover:bg-zinc-800">
          ← Retour
        </button>
        <div className="flex-1 text-center">
          <span className="font-black text-white">{circuitCfg.flag} {game.circuit}</span>
          <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded" style={{background:circuitCfg.color+'22',color:circuitCfg.color}}>
            {game.laps||1} tour{(game.laps||1)>1?'s':''}
          </span>
        </div>
        {game.status === 'playing' && (
          <div className="text-right text-[10px] text-zinc-500">
            <p>{myPlayer ? `C.${myPlayer.position}` : ''}</p>
            <p className="font-black" style={{color:circuitCfg.color}}>T.{(myPlayer?.lapsCompleted||0)+1}/{game.laps||1}</p>
          </div>
        )}
      </div>

      {/* Track */}
      <div className="rounded-2xl overflow-hidden" style={{border:`1px solid ${circuitCfg.color}33`}}>
        <TrackBoard game={game} circuitCfg={circuitCfg} />
      </div>

      {/* Event toast */}
      {showEvent && (
        <div className="rounded-xl px-4 py-3 flex items-center gap-3 font-black text-sm"
          style={{ background: (showEvent.color||'#e10600')+'1a', border:`1.5px solid ${showEvent.color||'#e10600'}66`, color: showEvent.color||'#e10600' }}>
          <span className="text-2xl shrink-0">{showEvent.emoji}</span>
          <div>
            <p>{showEvent.label}</p>
            {showEvent.extraInfo && <p className="font-normal text-xs text-zinc-300 mt-0.5">{showEvent.extraInfo}</p>}
          </div>
        </div>
      )}
      {!showEvent && myPlayer?.skippedTurns > 0 && (
        <div className="rounded-lg px-4 py-2 text-center text-xs bg-amber-400/10 border border-amber-400/30 text-amber-400 font-bold">
          ⏭ Tu dois passer {myPlayer.skippedTurns} tour{myPlayer.skippedTurns>1?'s':''}
        </div>
      )}
      {!showEvent && myPlayer?.rainTurns > 0 && (
        <div className="rounded-lg px-4 py-2 text-center text-xs bg-blue-400/10 border border-blue-400/30 text-blue-400 font-bold">
          🌧 Pluie — dé capé à 3 pendant {myPlayer.rainTurns} tour{myPlayer.rainTurns>1?'s':''}
        </div>
      )}

      {/* Players ranking */}
      <div className="space-y-2">
        {[...game.players]
          .sort((a,b) => {
            if (a.isFinished !== b.isFinished) return a.isFinished ? -1 : 1;
            const aTotal = (a.lapsCompleted||0)*TOTAL + a.position;
            const bTotal = (b.lapsCompleted||0)*TOTAL + b.position;
            return bTotal - aTotal;
          })
          .map((p, rank) => {
            const isActive = String(p.user) === String(curPlayer?.user) && game.status === 'playing';
            const done = (p.lapsCompleted||0)*TOTAL + p.position;
            const pct = Math.min(100, (done / totalCases) * 100);
            return (
              <div key={rank} className="rounded-xl px-3 py-2.5 border transition-all"
                style={{ borderColor: isActive ? circuitCfg.color : '#27272a', background: isActive ? circuitCfg.color+'10' : '#111113' }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-black text-zinc-600 w-4">P{rank+1}</span>
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{background:p.color}} />
                  <span className="font-bold text-sm text-white truncate flex-1">{p.username}</span>
                  {p.isFinished && <span className="text-[10px] font-black text-yellow-400 shrink-0">P{p.finishRank} 🏁</span>}
                  {p.skippedTurns > 0 && <span className="text-[9px] text-amber-400 shrink-0">⏭×{p.skippedTurns}</span>}
                  {p.rainTurns > 0 && <span className="text-[9px] text-blue-400 shrink-0">🌧×{p.rainTurns}</span>}
                  {isActive && !p.isFinished && (
                    <span className="text-[9px] font-black animate-pulse shrink-0" style={{color:circuitCfg.color}}>▶ son tour</span>
                  )}
                  <span className="text-[10px] text-zinc-500 shrink-0 font-mono">
                    T{(p.lapsCompleted||0)+1} C{p.position}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{ width:`${pct}%`, background:`linear-gradient(90deg,${p.color},${p.color}cc)`, boxShadow:`0 0 6px ${p.color}66` }} />
                </div>
              </div>
            );
          })}
      </div>

      {/* Dice + Roll */}
      {game.status === 'playing' && (
        <div className="rounded-2xl p-4 border bg-zinc-900/60" style={{borderColor: circuitCfg.color+'33'}}>
          <div className="flex items-center gap-4">
            <div className="shrink-0">
              <DiceFace value={diceAnim ?? game.lastRoll} size={64} rolling={rolling} accentColor={circuitCfg.color} />
            </div>
            <div className="flex-1">
              {isMyTurn ? (
                <>
                  <p className="text-xs font-bold mb-2" style={{color: circuitCfg.color}}>
                    {myPlayer?.skippedTurns > 0 ? `⏭ Tu passes ce tour (${myPlayer.skippedTurns} restant)` :
                     myPlayer?.rainTurns > 0    ? '🌧 Dé capé à 3 — Roule !' : "C'est ton tour — Roule !"}
                  </p>
                  <button onClick={handleRoll} disabled={rolling}
                    className="w-full py-3 rounded-xl font-black text-white transition-all active:scale-95 text-sm"
                    style={{
                      background: rolling ? '#2a2a2a' : `linear-gradient(135deg, ${circuitCfg.color}, ${circuitCfg.color}bb)`,
                      boxShadow: rolling ? 'none' : `0 4px 20px ${circuitCfg.color}44`,
                    }}>
                    {rolling ? '🎲 En route…' : myPlayer?.skippedTurns > 0 ? '⏭ Passer' : '🎲 Lancer le dé'}
                  </button>
                </>
              ) : (
                <div>
                  <p className="text-[10px] text-zinc-600 mb-1">En attente de</p>
                  <p className="font-black text-white">{curPlayer?.username}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Finished */}
      {game.status === 'finished' && (
        <div className="rounded-2xl p-5 text-center border"
          style={{ borderColor:'#FFD60055', background:'linear-gradient(135deg, #13100a, #1f1800)' }}>
          <p className="text-4xl mb-2">🏆</p>
          <p className="text-xl font-black text-yellow-400 mb-4">THE RACE — TERMINÉ !</p>
          <div className="space-y-1.5 mb-5">
            {[...game.players].sort((a,b) => (a.finishRank||99)-(b.finishRank||99)).map(p => (
              <div key={p.username} className="flex items-center justify-between px-3 py-2 rounded-xl"
                style={{background: p.finishRank<=3 ? '#ffffff08' : '#0a0a0a', border:`1px solid ${p.finishRank<=3?'#ffffff18':'#1a1a1a'}`}}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{p.finishRank===1?'🥇':p.finishRank===2?'🥈':p.finishRank===3?'🥉':'—'}</span>
                  <span className="font-bold text-white text-sm">{p.username}</span>
                </div>
                <span className="text-xs font-black" style={{color: p.finishRank<=3 ? '#FFD600' : '#555'}}>
                  {XP_REWARDS[p.finishRank] ? `+${XP_REWARDS[p.finishRank]} XP` : '0 XP'}
                </span>
              </div>
            ))}
          </div>
          <button onClick={onBack} className="w-full py-3 rounded-xl font-black text-white"
            style={{background:'linear-gradient(135deg, #e10600, #b80500)', boxShadow:'0 4px 20px rgba(225,6,0,0.4)'}}>
            Rejouer 🏎
          </button>
        </div>
      )}

      {/* Game log */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600 mb-1.5">Chrono de course</p>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 max-h-44 overflow-y-auto divide-y divide-zinc-800/50">
          {(game.log||[]).map((entry, i) => (
            <div key={i} className="flex items-start gap-2.5 px-3 py-2 text-xs">
              <span className="shrink-0 text-sm">{entry.emoji}</span>
              <span className="text-zinc-500">
                <span className="font-bold text-zinc-300">{entry.playerName}</span> {entry.message}
              </span>
            </div>
          ))}
          {(game.log||[]).length === 0 && <p className="text-zinc-600 text-center py-4 text-xs">La course commence…</p>}
        </div>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const TheRace = () => {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const { id }   = useParams();
  const [myGames, setMyGames]         = useState([]);
  const [activeGameId, setActiveGameId] = useState(id || null);

  useEffect(() => { api.get('/race-game/my-games').then(r => setMyGames(r.data)).catch(()=>{}); }, []);
  useEffect(() => { if (id) setActiveGameId(id); }, [id]);

  const handleStart = async (circuit, laps, friendIds = []) => {
    const { data } = await api.post('/race-game', { circuit, laps, friendIds });
    setActiveGameId(data._id);
    navigate(`/the-race/${data._id}`, { replace: true });
  };

  const handleBack = () => {
    setActiveGameId(null);
    navigate('/the-race', { replace: true });
    api.get('/race-game/my-games').then(r => setMyGames(r.data)).catch(()=>{});
  };

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <p className="text-zinc-400 text-sm">Connecte-toi pour jouer à THE RACE.</p>
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-4 py-4">
      {activeGameId
        ? <GameView gameId={activeGameId} onBack={handleBack} />
        : <Lobby onStart={handleStart} myGames={myGames} currentUserId={user?.id}
            onResume={gid => { setActiveGameId(gid); navigate(`/the-race/${gid}`, { replace: true }); }} />
      }
    </div>
  );
};

export default TheRace;
